import React from "react";
import {
  Box,
  Grid,
  Typography,
  Divider,
  Avatar,
  Stack,
  Chip,
  ButtonGroup,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Alert,
  CircularProgress,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import ContactPageOutlinedIcon from "@mui/icons-material/ContactPageOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import BeachAccessOutlinedIcon from "@mui/icons-material/BeachAccessOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { API_BASE_URL } from "../../config";

  /* ======= Box-Höhen oben separat einstellen ======= */
  const BOX_HOURS_MIN_H = 260;    // Höhe Box 1 (KPI + Chart)
  const BOX_HOLIDAYS_MIN_H = 180; // Höhe Box 2 (Ferien)
  
  /* -------- helpers -------- */
  function formatHours(x) {
    const n = Number(x || 0);
    return Number.isFinite(n) ? (n % 1 === 0 ? String(n) : n.toFixed(1)) : "0";
  }

  function formatFileSize(bytes) {
    const n = Number(bytes || 0);
    if (!Number.isFinite(n) || n <= 0) return "0 B";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  }

  /** Monats-Barchart (SVG, Dashboard-Style) */
  function MonthlyBarChart({ data, t, targetWorkingHours }) {
    const theme = useTheme();
    const H = 200, PAD_T = 10, PAD_B = 22, AXIS_W = 44, GAP = 10, COL_W = 36, BAR_FRAC = 0.62;

    const months = Array.isArray(data) ? data : [];

    // Zielwert aus Prop
    const target = Number(targetWorkingHours) || 0;

    // ✅ Max-Wert inkl. Balken UND Ziel-Linie
    const rawMax = Math.max(0, target, ...months.map((d) => +d.h || 0));
    const allZero = rawMax <= 0;
    const max = allZero ? 1 : rawMax;

    const innerWidth = 12 * COL_W + (12 - 1) * GAP;
    const width = innerWidth + AXIS_W;
    const innerH = H - PAD_T - PAD_B;

    const yPx = (val) => PAD_B + (val / max) * innerH;
    const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * max);

    const gridColor = alpha(theme.palette.text.primary, 0.12);
    const labelColor = alpha(theme.palette.text.primary, 0.7);
    const barColor = alpha(theme.palette.info.main, 0.9);
    const targetColor = theme.palette.success.main;

    const centerY = PAD_T + innerH / 1.2;

    return (
      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <svg viewBox={`0 0 ${width} ${H}`} width="100%" height={H} style={{ display: "block" }}>
          {/* horizontale Grid-Linien */}
          {ticks.map((tck, i) => (
            <line
              key={i}
              x1={AXIS_W}
              x2={width}
              y1={H - yPx(tck)}
              y2={H - yPx(tck)}
              stroke={gridColor}
            />
          ))}

          {/* Y-Achsen-Beschriftung */}
          {ticks.map((tck, i) => (
            <text
              key={`yt-${i}`}
              x={AXIS_W - 8}
              y={H - yPx(tck) + 4}
              textAnchor="end"
              fontSize="11"
              fill={labelColor}
              style={{ fontVariantNumeric: "tabular-nums" }}
            >
              {allZero ? 0 : Math.round(tck)}
            </text>
          ))}

          {/* Y-Achsen-Label */}
          <text
            x={14}
            y={centerY}
            transform={`rotate(-90 14 ${centerY})`}
            fontSize="11"
            fill={labelColor}
          >
            {t("mitarbeiterakte.chart_y_label")}
          </text>

          {/* Balken */}
          {months.map((d, i) => {
            const x = AXIS_W + i * (COL_W + GAP);
            const bw = COL_W * BAR_FRAC;
            const bh = (Number(d.h) || 0) / max * innerH;
            const bx = x + (COL_W - bw) / 2;
            const by = H - PAD_B - bh;

            return (
              <g key={`${d.m}-${i}`}>
                <rect x={bx} y={by} width={bw} height={bh} rx="4" fill={barColor}>
                  <title>{`${d.m}: ${Math.round(Number(d.h) || 0)} h`}</title>
                </rect>
                <text
                  x={x + COL_W / 2}
                  y={H - 6}
                  textAnchor="middle"
                  fontSize="11"
                  fill={labelColor}
                >
                  {d.m}
                </text>
              </g>
            );
          })}

          {/* Ziel-Linie für monatliche Soll-Stunden */}
          {target > 0 && (
            <g>
              <line
                x1={AXIS_W}
                x2={width}
                y1={H - yPx(target)}
                y2={H - yPx(target)}
                stroke={targetColor}
                strokeWidth={1.5}
                strokeDasharray="4 4"
              >
                <title>{`Ziel: ${Math.round(target)} h / Monat`}</title>
              </line>
            </g>
          )}
        </svg>
        {target > 0 && (
          <Box
            sx={{
              pl: `5px`,
              display: "flex",
              justifyContent: "flex-start",
            }}
          >
            <Box
              sx={(t) => ({
                display: "inline-flex",
                alignItems: "center",
                borderRadius: 999,
                px: 1.5,
                bgcolor: alpha(
                  targetColor,
                  t.palette.mode === "dark" ? 0.16 : 0.08
                ),
              })}
            >
              <Box
                component="span"
                sx={{
                  display: "inline-block",
                  width: 28,
                  borderTopStyle: "dashed",
                  borderTopWidth: 1.5,
                  borderColor: targetColor,
                  mr: 1,
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {t("mitarbeiterakte.target_hours_label") || "Soll-Stunden"}
              </Typography>
            </Box>
          </Box>
        )}

      </Box>
    );
  }


/* -------- Component -------- */
export default function UserFactSheetDocument({
  user = {},
  title = "",
  canManageCompanyDocs = false,
  documentsEnabled = false,
}) {
  const { t, i18n } = useTranslation();
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ") || "—";
  const initials = `${user.first_name?.[0] ?? ""}${user.last_name?.[0] ?? ""}`.toUpperCase() || "–";
  const email = user.email || "—";
  const phone = user.phone_number || user.phone || "—";

  const currentYear = new Date().getFullYear();
  const [year, setYear] = React.useState(currentYear);
  const [hoursYear, setHoursYear] = React.useState(0);
  const [avgPerMonth, setAvgPerMonth] = React.useState(0);
  const [targetWorkingHours, setTargetWorkingHours] = React.useState(0);

  const depts = React.useMemo(() => {
    const r = user.ranking;
    if (!r || typeof r !== "object") return [];
  
    const entries = Object.entries(r); // [ [name, value], ... ]
  
    // Helper: valid rank = positive number; null/undefined/"" => no rank
    const getRank = (val) => {
      if (val === null || val === undefined || val === "") return null;
      const n = Number(val);
      // Only treat positive numbers as valid ranks
      return Number.isFinite(n) && n > 0 ? n : null;
    };
  
    // Check if at least one real rank exists
    const hasAnyRank = entries.some(([, val]) => getRank(val) !== null);
  
    // Sorting
    entries.sort(([nameA, valA], [nameB, valB]) => {
      const rankA = getRank(valA);
      const rankB = getRank(valB);
  
      if (hasAnyRank) {
        // Ranked first (1, 2, 3, ...), unranked at the end
        const keyA = rankA === null ? Infinity : rankA;
        const keyB = rankB === null ? Infinity : rankB;
  
        if (keyA !== keyB) return keyA - keyB; // lower rank first
        // same rank -> alphabetical
        return nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
      } else {
        // No ranks at all → sort purely by name
        return nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
      }
    });
  
    // Build display labels
    return entries.map(([name, val]) => {
      const rank = getRank(val);
      // If no valid rank → just the name
      return rank === null ? name : `${name} (${rank})`;
    });
  }, [user.ranking]);  



  const monthLabel = React.useCallback(
    (idx) =>
      new Date(Date.UTC(2000, idx, 1)).toLocaleString(i18n.language, {
        month: "short",
      }),
    [i18n.language]
  );

  const [monthlyData, setMonthlyData] = React.useState(() =>
    Array.from({ length: 12 }, (_, i) => ({ m: monthLabel(i), h: 0 }))
  );

  // Aktualisiere Monatslabels, wenn Sprache wechselt
  React.useEffect(() => {
    setMonthlyData((prev) => prev.map((d, i) => ({ ...d, m: monthLabel(i) })));
  }, [i18n.language, monthLabel]);

  const [vacTotals, setVacTotals] = React.useState({
    days_year: 0,
    days_past: 0,
    days_future: 0,
  });
  const [vacPast, setVacPast] = React.useState([]);
  const [vacUpcoming, setVacUpcoming] = React.useState([]);
  const [documents, setDocuments] = React.useState([]);
  const [documentsLoading, setDocumentsLoading] = React.useState(false);
  const [documentsError, setDocumentsError] = React.useState("");
  const [uploading, setUploading] = React.useState(false);
  const [companyDocuments, setCompanyDocuments] = React.useState([]);
  const [companyDocumentsLoading, setCompanyDocumentsLoading] = React.useState(false);
  const [companyDocumentsError, setCompanyDocumentsError] = React.useState("");
  const [companyUploading, setCompanyUploading] = React.useState(false);
  const [openPdfViewer, setOpenPdfViewer] = React.useState(false);
  const [pdfViewerUrl, setPdfViewerUrl] = React.useState("");
  const [pdfViewerTitle, setPdfViewerTitle] = React.useState("");
  const fileInputRef = React.useRef(null);
  const companyFileInputRef = React.useRef(null);

  const fmtDocDate = React.useCallback(
    (iso) => {
      if (!iso) return "—";
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return "—";
      return d.toLocaleString(i18n.language || "de-CH");
    },
    [i18n.language]
  );

  // Datumsbereich hübsch & lokalisiert
  const fmtRange = React.useCallback(
    ({ start, end, days }) => {
      const s = new Date(start);
      const e = new Date(end);
      const sameMonth =
        s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();

      const sd = s.toLocaleDateString(i18n.language, { day: "numeric" });
      const sm = s.toLocaleDateString(i18n.language, { month: "short" });
      const ed = e.toLocaleDateString(i18n.language, { day: "numeric" });
      const em = e.toLocaleDateString(i18n.language, { month: "short" });

      const label = sameMonth ? `${sd}–${ed} ${sm}` : `${sd} ${sm} – ${ed} ${em}`;
      return `${label} (${days})`;
    },
    [i18n.language]
  );

  React.useEffect(() => {
    if (!user?.id) return;
    let cancel = false;
    (async () => {
      try {
        const token = localStorage.getItem("session_token");
        const res = await axios.get(
          `${API_BASE_URL}/api/employee-file/${user.id}/summary`,
          { params: { year }, headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        if (cancel) return;
        const arr = (res.data?.monthly_hours || []).map((h, idx) => ({
          m: monthLabel(idx),
          h: Number(h || 0),
        }));
        setMonthlyData(arr);
        setHoursYear(Number(res.data?.totals?.hours_year || 0));
        setAvgPerMonth(Number(res.data?.totals?.avg_per_month || 0));
        setTargetWorkingHours(Number(res.data?.user?.employment_hours || 0));
      } catch {
        setMonthlyData(Array.from({ length: 12 }, (_, i) => ({ m: monthLabel(i), h: 0 })));
        setHoursYear(0);
        setAvgPerMonth(0);
      }
    })();
    return () => { cancel = true; };
  }, [user?.id, year, monthLabel]);

  React.useEffect(() => {
    if (!user?.id) return;
    let cancel = false;
    (async () => {
      try {
        const token = localStorage.getItem("session_token");
        const res = await axios.get(
          `${API_BASE_URL}/api/employee-file/${user.id}/holidays`,
          { params: { year }, headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        if (cancel) return;
        setVacTotals(res.data?.totals || { days_year: 0, days_past: 0, days_future: 0 });
        setVacPast(Array.isArray(res.data?.past) ? res.data.past : []);
        setVacUpcoming(Array.isArray(res.data?.upcoming) ? res.data.upcoming : []);
      } catch {
        setVacTotals({ days_year: 0, days_past: 0, days_future: 0 });
        setVacPast([]);
        setVacUpcoming([]);
      }
    })();
    return () => { cancel = true; };
  }, [user?.id, year]);

  const loadDocuments = React.useCallback(async () => {
    if (!documentsEnabled || !user?.id) {
      setDocuments([]);
      setDocumentsError("");
      setDocumentsLoading(false);
      return;
    }
    setDocumentsLoading(true);
    setDocumentsError("");
    try {
      const token = localStorage.getItem("session_token");
      const res = await axios.get(`${API_BASE_URL}/api/web/team/users/${user.id}/documents`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setDocuments(Array.isArray(res.data?.documents) ? res.data.documents : []);
    } catch {
      setDocuments([]);
      setDocumentsError(t("mitarbeiterakte.documents_load_error"));
    } finally {
      setDocumentsLoading(false);
    }
  }, [documentsEnabled, user?.id, t]);

  React.useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const loadCompanyDocuments = React.useCallback(async () => {
    if (!documentsEnabled) {
      setCompanyDocuments([]);
      setCompanyDocumentsError("");
      setCompanyDocumentsLoading(false);
      return;
    }
    setCompanyDocumentsLoading(true);
    setCompanyDocumentsError("");
    try {
      const token = localStorage.getItem("session_token");
      const res = await axios.get(`${API_BASE_URL}/api/company-documents`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setCompanyDocuments(Array.isArray(res.data?.documents) ? res.data.documents : []);
    } catch {
      setCompanyDocuments([]);
      setCompanyDocumentsError(t("mitarbeiterakte.company_documents_load_error"));
    } finally {
      setCompanyDocumentsLoading(false);
    }
  }, [documentsEnabled, t]);

  React.useEffect(() => {
    loadCompanyDocuments();
  }, [loadCompanyDocuments]);

  React.useEffect(() => {
    return () => {
      if (pdfViewerUrl) URL.revokeObjectURL(pdfViewerUrl);
    };
  }, [pdfViewerUrl]);

  const onUploadClick = () => {
    if (!documentsEnabled) return;
    fileInputRef.current?.click();
  };

  const onCompanyUploadClick = () => {
    if (!documentsEnabled) return;
    companyFileInputRef.current?.click();
  };

  const openPdfBlob = (blob, titleText) => {
    if (pdfViewerUrl) URL.revokeObjectURL(pdfViewerUrl);
    const blobUrl = URL.createObjectURL(blob);
    setPdfViewerTitle(titleText || "PDF");
    setPdfViewerUrl(blobUrl);
    setOpenPdfViewer(true);
  };

  const onFileSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!documentsEnabled || !file || !user?.id) return;

    setUploading(true);
    setDocumentsError("");
    try {
      const token = localStorage.getItem("session_token");
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API_BASE_URL}/api/employee-file/${user.id}/documents`, formData, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "Content-Type": "multipart/form-data",
        },
      });
      await loadDocuments();
    } catch (err) {
      const msg = err?.response?.data?.message || t("mitarbeiterakte.documents_upload_error");
      setDocumentsError(msg);
    } finally {
      setUploading(false);
    }
  };

  const onViewDocument = async (doc) => {
    if (!documentsEnabled || !doc?.id || !user?.id) return;
    try {
      const token = localStorage.getItem("session_token");
      const res = await axios.get(
        `${API_BASE_URL}/api/employee-file/${user.id}/documents/${doc.id}`,
        {
          responseType: "blob",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      openPdfBlob(res.data, doc.file_name || "PDF");
    } catch {
      setDocumentsError(t("mitarbeiterakte.documents_view_error"));
    }
  };

  const onDeleteDocument = async (docId) => {
    if (!documentsEnabled || !docId || !user?.id) return;
    try {
      const token = localStorage.getItem("session_token");
      await axios.delete(`${API_BASE_URL}/api/employee-file/${user.id}/documents/${docId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (openPdfViewer && documents.some((d) => d.id === docId)) {
        setOpenPdfViewer(false);
        if (pdfViewerUrl) URL.revokeObjectURL(pdfViewerUrl);
        setPdfViewerUrl("");
        setPdfViewerTitle("");
      }
      await loadDocuments();
    } catch {
      setDocumentsError(t("mitarbeiterakte.documents_delete_error"));
    }
  };

  const onCompanyFileSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!documentsEnabled || !file) return;

    setCompanyUploading(true);
    setCompanyDocumentsError("");
    try {
      const token = localStorage.getItem("session_token");
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API_BASE_URL}/api/company-documents`, formData, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "Content-Type": "multipart/form-data",
        },
      });
      await loadCompanyDocuments();
    } catch (err) {
      const msg = err?.response?.data?.message || t("mitarbeiterakte.company_documents_upload_error");
      setCompanyDocumentsError(msg);
    } finally {
      setCompanyUploading(false);
    }
  };

  const onViewCompanyDocument = async (doc) => {
    if (!documentsEnabled || !doc?.id) return;
    try {
      const token = localStorage.getItem("session_token");
      const res = await axios.get(
        `${API_BASE_URL}/api/company-documents/${doc.id}`,
        {
          responseType: "blob",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      openPdfBlob(res.data, doc.file_name || "PDF");
    } catch {
      setCompanyDocumentsError(t("mitarbeiterakte.company_documents_view_error"));
    }
  };

  const onDeleteCompanyDocument = async (docId) => {
    if (!documentsEnabled || !docId) return;
    try {
      const token = localStorage.getItem("session_token");
      await axios.delete(`${API_BASE_URL}/api/company-documents/${docId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      await loadCompanyDocuments();
    } catch {
      setCompanyDocumentsError(t("mitarbeiterakte.company_documents_delete_error"));
    }
  };

  const closePdfViewer = () => {
    setOpenPdfViewer(false);
    if (pdfViewerUrl) URL.revokeObjectURL(pdfViewerUrl);
    setPdfViewerUrl("");
    setPdfViewerTitle("");
  };

  const headerTitle = title || t("mitarbeiterakte.title");

  return (
    <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", minHeight: 0, position: "relative" }}>
      {/* Header */}
      <Box sx={{ px: 3, py: 2, borderBottom: (t) => `1px solid ${t.palette.divider}` }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <ContactPageOutlinedIcon fontSize="medium" sx={{ color: "primary.main" }} aria-hidden />
          <Typography variant="h6" fontWeight={700}>
            {headerTitle} {t("mitarbeiterakte.name_separator")} {fullName}
          </Typography>
        </Stack>
      </Box>

      {/* Mittelbereich */}
      <Box sx={{ flex: 1, minHeight: 0, position: "relative" }}>
        <Box
          aria-hidden
          sx={(t) => ({
            position: "absolute", inset: 0, zIndex: 0, pointerEvents: "none", display: { xs: "none", md: "block" },
            background: `
              linear-gradient(
                to right,
                ${t.palette.background.box} 0%,
                ${t.palette.background.box} 29.1667%,
                transparent 29.1667%
              )`,
            [t.breakpoints.up("lg")]: {
              background: `
                linear-gradient(
                  to right,
                  ${t.palette.background.box} 0%,
                  ${t.palette.background.box} 25%,
                  transparent 25%
                )`,
            },
          })}
        />

        <Box sx={{ position: "relative", zIndex: 1, height: "100%", overflow: "auto", pb: 2 }}>
          <Grid container sx={{ alignItems: "stretch" }}>
            {/* Linke Spalte */}
            <Grid
              item
              xs={12}
              md={3.5}
              lg={3}
              sx={{
                borderRight: { md: (t) => `1px solid ${t.palette.divider}` },
                bgcolor: { xs: "background.box", md: "transparent" },
                alignSelf: "stretch",
              }}
            >
              <Box sx={{ p: 3, display: "flex", flexDirection: "column" }}>
                <Stack spacing={1.25} alignItems="center" sx={{ textAlign: "center" }}>
                  <Avatar
                    sx={{
                      width: 96, height: 96, fontSize: 32,
                      bgcolor: (t) => alpha(t.palette.info.main, t.palette.mode === "dark" ? 0.25 : 0.12),
                      color: (t) => t.palette.info.main, fontWeight: 800,
                    }}
                  >
                    {initials}
                  </Avatar>
                  <Typography variant="h6" fontWeight={700}>{fullName}</Typography>
                </Stack>

                <Divider sx={{ my: 2.5 }} />

                <InfoBlock label={t("mitarbeiterakte.email")} icon={<EmailOutlinedIcon fontSize="small" />} value={email} />
                <InfoBlock label={t("mitarbeiterakte.phone")} icon={<PhoneOutlinedIcon fontSize="small" />} value={phone} />
                <InfoBlock
                  label={t("mitarbeiterakte.departments_skills")}
                  icon={<WorkOutlineOutlinedIcon fontSize="small" />}
                  value={
                    depts.length ? (
                      <Stack direction="row" flexWrap="wrap" gap={1} mt={0.25}>
                        {depts.map((d) => (
                          <Chip key={d} label={d} size="small" variant="outlined" sx={{ borderColor: "divider" }} />
                        ))}
                      </Stack>
                    ) : "—"
                  }
                />
                <Box sx={{ flexGrow: 1 }} />
              </Box>
            </Grid>

            {/* Rechte Fläche */}
            <Grid item xs={12} md={8.5} lg={9} sx={{ alignSelf: "stretch" }}>
              <Box sx={{ p: 3, minHeight: 0 }}>
                {/* Überschrift + Year-Switch */}
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                  <Typography variant="h4" fontWeight={700}>{t("mitarbeiterakte.overview", { year })}</Typography>
                  <ButtonGroup variant="outlined" size="small" aria-label={t("mitarbeiterakte.year_switch_aria")}>
                    <Button onClick={() => setYear((y) => y - 1)} startIcon={<ChevronLeftIcon />}>{year - 1}</Button>
                    <Button variant="contained" disableElevation>{year}</Button>
                    <Button onClick={() => setYear((y) => y + 1)} endIcon={<ChevronRightIcon />}>{year + 1}</Button>
                  </ButtonGroup>
                </Stack>

                {/* Box 1: KPI + Chart */}
                <Box sx={{ border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 2.5, bgcolor: "background.paper", p: 2, minHeight: BOX_HOURS_MIN_H }}>
                  <Grid container spacing={2} alignItems="stretch" sx={{ minHeight: BOX_HOURS_MIN_H }}>
                    <Grid item xs={12} md={4} sx={{ display: "flex" }}>
                      <Box sx={{ my: "auto", display: "flex", flexDirection: "column" }}>
                        <Typography variant="body2" color="text.secondary">{t("mitarbeiterakte.hours_ytd")}</Typography>
                        <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5 }}>{formatHours(hoursYear)} h</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                          {t("mitarbeiterakte.avg_per_month", { value: formatHours(avgPerMonth) })}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{t("mitarbeiterakte.monthly_hours")}</Typography>
                      <MonthlyBarChart data={monthlyData} t={t} targetWorkingHours={targetWorkingHours} />
                    </Grid>
                  </Grid>
                </Box>

                {/* Box 2: Ferien */}
                <Box sx={{ mt: 2, border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 2.5, bgcolor: "background.paper", p: 2, minHeight: BOX_HOLIDAYS_MIN_H }}>
                  <Grid container spacing={2} alignItems="stretch" sx={{ minHeight: BOX_HOLIDAYS_MIN_H }}>
                    <Grid item xs={12} md={4} sx={{ display: "flex" }}>
                      <Box sx={{ my: "auto", display: "flex", flexDirection: "column" }}>
                        <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <BeachAccessOutlinedIcon fontSize="small" /> {t("mitarbeiterakte.holidays_ytd")}
                        </Typography>
                        <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5 }}>
                          {vacTotals.days_year} {t("mitarbeiterakte.day_label", { count: vacTotals.days_year })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "flex", alignItems: "center", gap: 0.75 }}>
                          <EventAvailableOutlinedIcon fontSize="small" /> {t("mitarbeiterakte.already_taken")} <b>{vacTotals.days_past}</b>
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: "flex", alignItems: "center", gap: 0.75 }}>
                          <ScheduleOutlinedIcon fontSize="small" /> {t("mitarbeiterakte.planned_from_today")} <b>{vacTotals.days_future}</b>
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={8}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {t("mitarbeiterakte.taken_holidays")}
                          </Typography>
                          {vacPast.length ? (
                            <Stack direction="row" flexWrap="wrap" gap={1}>
                              {vacPast.map((r, i) => (
                                <Chip key={`p-${i}`} size="small" variant="outlined" label={fmtRange(r)} sx={{ borderColor: "divider" }} />
                              ))}
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.secondary">{t("mitarbeiterakte.no_entries")}</Typography>
                          )}
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {t("mitarbeiterakte.upcoming_holidays")}
                          </Typography>
                          {vacUpcoming.length ? (
                            <Stack direction="row" flexWrap="wrap" gap={1}>
                              {vacUpcoming.map((r, i) => (
                                <Chip key={`u-${i}`} size="small" variant="outlined" color="info" label={fmtRange(r)} />
                              ))}
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.secondary">{t("mitarbeiterakte.no_entries")}</Typography>
                          )}
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Box>

                {documentsEnabled ? (
                  <>
                    {/* Box 3: Dokumente */}
                    <Box sx={{ mt: 2, border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 2.5, bgcolor: "background.paper", p: 2 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                    <Typography variant="body1" fontWeight={700}>
                      {t("mitarbeiterakte.documents_title")}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={uploading ? <CircularProgress size={14} /> : <UploadFileOutlinedIcon />}
                      onClick={onUploadClick}
                      disabled={uploading}
                    >
                      {t("mitarbeiterakte.documents_upload")}
                    </Button>
                  </Stack>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf,.pdf"
                    style={{ display: "none" }}
                    onChange={onFileSelected}
                  />

                  {documentsError ? (
                    <Alert severity="error" sx={{ mb: 1.5 }}>
                      {documentsError}
                    </Alert>
                  ) : null}

                  {documentsLoading ? (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CircularProgress size={18} />
                      <Typography variant="body2" color="text.secondary">
                        {t("mitarbeiterakte.documents_loading")}
                      </Typography>
                    </Stack>
                  ) : documents.length ? (
                    <Stack spacing={1}>
                      {documents.map((doc) => (
                        <Box
                          key={doc.id}
                          sx={{
                            border: (t) => `1px solid ${t.palette.divider}`,
                            borderRadius: 1.5,
                            p: 1.25,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <PictureAsPdfOutlinedIcon sx={{ color: "error.main" }} fontSize="small" />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {doc.file_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatFileSize(doc.file_size)} • {fmtDocDate(doc.uploaded_at)}
                            </Typography>
                          </Box>
                          <Tooltip title={t("mitarbeiterakte.documents_view")}>
                            <IconButton size="small" onClick={() => onViewDocument(doc)}>
                              <VisibilityOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t("mitarbeiterakte.documents_delete")}>
                            <IconButton size="small" color="error" onClick={() => onDeleteDocument(doc.id)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t("mitarbeiterakte.documents_empty")}
                    </Typography>
                  )}
                    </Box>

                    {/* Box 4: Allgemeine Dokumente */}
                    <Box sx={{ mt: 2, border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 2.5, bgcolor: "background.paper", p: 2 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                    <Typography variant="body1" fontWeight={700}>
                      {t("mitarbeiterakte.company_documents_title")}
                    </Typography>
                    {canManageCompanyDocs ? (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={companyUploading ? <CircularProgress size={14} /> : <UploadFileOutlinedIcon />}
                        onClick={onCompanyUploadClick}
                        disabled={companyUploading}
                      >
                        {t("mitarbeiterakte.company_documents_upload")}
                      </Button>
                    ) : null}
                  </Stack>

                  <input
                    ref={companyFileInputRef}
                    type="file"
                    accept="application/pdf,.pdf"
                    style={{ display: "none" }}
                    onChange={onCompanyFileSelected}
                  />

                  {companyDocumentsError ? (
                    <Alert severity="error" sx={{ mb: 1.5 }}>
                      {companyDocumentsError}
                    </Alert>
                  ) : null}

                  {companyDocumentsLoading ? (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CircularProgress size={18} />
                      <Typography variant="body2" color="text.secondary">
                        {t("mitarbeiterakte.company_documents_loading")}
                      </Typography>
                    </Stack>
                  ) : companyDocuments.length ? (
                    <Stack spacing={1}>
                      {companyDocuments.map((doc) => (
                        <Box
                          key={`company-doc-${doc.id}`}
                          sx={{
                            border: (t) => `1px solid ${t.palette.divider}`,
                            borderRadius: 1.5,
                            p: 1.25,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <PictureAsPdfOutlinedIcon sx={{ color: "error.main" }} fontSize="small" />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {doc.file_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatFileSize(doc.file_size)} • {fmtDocDate(doc.uploaded_at)}
                            </Typography>
                          </Box>
                          <Tooltip title={t("mitarbeiterakte.company_documents_view")}>
                            <IconButton size="small" onClick={() => onViewCompanyDocument(doc)}>
                              <VisibilityOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {canManageCompanyDocs ? (
                            <Tooltip title={t("mitarbeiterakte.company_documents_delete")}>
                              <IconButton size="small" color="error" onClick={() => onDeleteCompanyDocument(doc.id)}>
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t("mitarbeiterakte.company_documents_empty")}
                    </Typography>
                  )}
                    </Box>
                  </>
                ) : null}
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box aria-hidden sx={{ position: "absolute", left: 0, right: 0, bottom: 0, borderTop: (t) => `1px solid ${t.palette.divider}`, bgcolor: (t) => t.palette.background.paper, zIndex: 2 }} />
      </Box>

      <Dialog
        open={openPdfViewer}
        onClose={closePdfViewer}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>{pdfViewerTitle || t("mitarbeiterakte.documents_view")}</DialogTitle>
        <DialogContent sx={{ p: 0, height: "80vh" }}>
          {pdfViewerUrl ? (
            <iframe
              title="employee-pdf-viewer"
              src={pdfViewerUrl}
              style={{ width: "100%", height: "100%", border: 0 }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

/* ---- helpers (UI) ---- */
function InfoBlock({ label, icon, value }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>{label}</Typography>
      <Stack direction="row" alignItems="flex-start" spacing={1.25}>
        <Box sx={{ mt: "2px", color: "text.secondary" }}>{icon}</Box>
        {typeof value === "string" ? (
          <Typography variant="body2" sx={{ wordBreak: "break-word" }}>{value}</Typography>
        ) : value}
      </Stack>
    </Box>
  );
}
